import { useState, useCallback, useEffect, useMemo } from 'react';

/**
 * useBulkSelect — Set-based selection state for tables/lists with bulk actions.
 *
 * Extracts the shared pattern used in Matters.js, Tasks.js, TimeReviewQueue.js,
 * TimeEntries.js.  Preserves selection across filter tweaks (per the 2026-07-28
 * TimeEntries fix) by pruning only ids no longer visible instead of resetting on
 * every rows change.
 *
 * Usage:
 *   const rows = filteredMatters;
 *   const { selectedIds, toggle, toggleAll, clear, has, size,
 *           isAllSelected, isAnySelected } = useBulkSelect(rows);
 *
 *   <input type="checkbox"
 *          checked={isAllSelected}
 *          onChange={(e) => toggleAll(rows, e.target.checked)} />
 *   {rows.map(r => (
 *     <input type="checkbox"
 *            checked={has(r.id)}
 *            onChange={() => toggle(r.id)} />
 *   ))}
 *
 * Params:
 *   rows     — the currently VISIBLE rows (post-filter).  Used to auto-prune
 *              selections whose row has scrolled out of the visible set AND
 *              to compute isAllSelected.
 *   options.rowKey — property name to read the id from each row (default 'id').
 *
 * Returns:
 *   selectedIds     Set<id>
 *   toggle(id)      add/remove a single id
 *   toggleAll(rows, checked)  add every visible row's id (or remove them)
 *   clear()         reset to empty Set
 *   has(id)         boolean membership check
 *   size            number of selected ids
 *   isAllSelected   true when every visible row's id is selected (false when rows is empty)
 *   isAnySelected   size > 0
 *   pruneToVisible(rows)  manual prune — auto-fires already via effect
 */
export default function useBulkSelect(rows, { rowKey = 'id' } = {}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((visibleRows, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const list = Array.isArray(visibleRows) ? visibleRows : [];
      if (checked) {
        for (const r of list) {
          if (r && r[rowKey] != null) next.add(r[rowKey]);
        }
      } else {
        for (const r of list) {
          if (r && r[rowKey] != null) next.delete(r[rowKey]);
        }
      }
      return next;
    });
  }, [rowKey]);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const has = useCallback((id) => selectedIds.has(id), [selectedIds]);

  const pruneToVisible = useCallback((visibleRows) => {
    const list = Array.isArray(visibleRows) ? visibleRows : [];
    const visibleSet = new Set(list.map((r) => r && r[rowKey]).filter((id) => id != null));
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (visibleSet.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [rowKey]);

  // Cheap change-signature: length + first + last id.  For a caller whose
  // rows array reference is stable this is a no-op (memo returns the same
  // key), and for a caller who rebuilds rows on every render the cost is
  // O(1) instead of the previous O(n log n) sort-and-join.
  //
  // NOTE — this heuristic can miss a same-length swap that keeps the head
  // + tail ids unchanged (e.g. rows[1]..rows[n-2] reordered / substituted).
  // The auto-prune only DROPS selected ids no longer visible; it never
  // ADDS anything, so a missed change means at worst a stale selected id
  // survives until the next real change.  Callers who care should memoize
  // `rows` upstream so this hook sees a stable reference — that's the
  // recommended pattern regardless.
  const visibleIdsKey = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    if (list.length === 0) return '';
    const first = list[0]?.[rowKey] ?? '';
    const last = list[list.length - 1]?.[rowKey] ?? '';
    return `${list.length}|${first}|${last}`;
  }, [rows, rowKey]);

  useEffect(() => {
    const list = Array.isArray(rows) ? rows : [];
    const visibleSet = new Set(
      list.map((r) => r && r[rowKey]).filter((id) => id != null)
    );
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (visibleSet.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
    // Depend on the cheap key — pruning uses the CURRENT `rows` closure so
    // an in-progress render still gets accurate ids even though the effect
    // only refires when the signature changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleIdsKey, rowKey]);

  const size = selectedIds.size;
  const isAnySelected = size > 0;

  const isAllSelected = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    if (list.length === 0) return false;
    return list.every((r) => r && r[rowKey] != null && selectedIds.has(r[rowKey]));
  }, [rows, rowKey, selectedIds]);

  return {
    selectedIds,
    toggle,
    toggleAll,
    clear,
    has,
    size,
    isAllSelected,
    isAnySelected,
    pruneToVisible,
  };
}
